import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ArrowRight } from 'lucide-react';
import { fetchInterviews } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { InterviewPreview } from '../types/interviews';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export function InterviewSlider() {
  const { userInfo } = useAuth();
  const [interviews, setInterviews] = useState<InterviewPreview[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    const loadInterviews = async () => {
      if (!userInfo) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetchInterviews(1, 6, undefined, 'date', userInfo);
        const interviewsData = Array.isArray(response) ? response : (response as any)?.data || [];
        const validInterviews = interviewsData.filter((item: any) => item && !item.error);
        setInterviews(validInterviews);
        setError(null);
      } catch (error) {
        console.error('Error loading interviews:', error);
        setError(error instanceof Error ? error.message : 'Erreur de chargement');
      } finally {
        setIsLoading(false);
      }
    };

    loadInterviews();
  }, [userInfo]);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      setCurrentIndex((prev) => (prev + 1) % interviews.length);
    } else if (distance < -minSwipeDistance) {
      setCurrentIndex((prev) => (prev - 1 + interviews.length) % interviews.length);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Recits de Pvtistes
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Inspirez-vous des experiences vecues par la communaute
          </p>
        </div>
        <div className="hidden md:grid md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
              <div className="h-56 bg-gray-200 dark:bg-gray-700"></div>
              <div className="p-5 bg-white dark:bg-gray-800 space-y-3">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
        <div className="md:hidden rounded-2xl overflow-hidden animate-pulse">
          <div className="h-72 bg-gray-200 dark:bg-gray-700"></div>
        </div>
      </div>
    );
  }

  if (error || !userInfo || interviews.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center justify-center gap-3">
          <span>Recits de</span>
          <img
            src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_positive_fullcolor.png"
            alt="Pvtistes"
            className="h-7 sm:h-8 w-auto dark:hidden"
          />
          <img
            src="https://pvtistes.net/wp-content/uploads/2025/01/rgb_pvtistes_logo_negative_white.png"
            alt="Pvtistes"
            className="h-7 sm:h-8 w-auto hidden dark:block"
          />
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Inspirez-vous des experiences vecues par la communaute
        </p>
      </div>

      {/* Desktop - 3 cards */}
      <div className="hidden md:grid md:grid-cols-3 gap-4">
        {interviews.slice(0, 3).map((item) => (
          <Link
            key={item.id}
            to={`/interviews/${item.id}`}
            className="group overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700/50 hover:shadow-lg transition-shadow duration-300"
          >
            <div className="relative h-56 overflow-hidden">
              <img
                src={item.thumbnail || item.cover}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="inline-flex items-center px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-lg text-white text-xs font-medium">
                  {item.interviewee.firstname}
                </span>
              </div>
            </div>
            <div className="p-5">
              <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 leading-snug">
                {item.title}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-3 leading-relaxed">
                {item.shortDescription}
              </p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500 text-xs">
                  <Clock className="w-3 h-3" />
                  <span>
                    {formatDistanceToNow(new Date(item.created), {
                      addSuffix: true,
                      locale: fr
                    })}
                  </span>
                </div>
                <span className="text-xs font-medium text-primary-600 dark:text-primary-400 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Lire
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Mobile slider */}
      <div className="md:hidden relative">
        <div
          className="overflow-hidden rounded-2xl"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {interviews.map((item) => (
              <div key={item.id} className="w-full flex-shrink-0 px-0.5">
                <Link
                  to={`/interviews/${item.id}`}
                  className="block relative overflow-hidden rounded-2xl"
                >
                  <div className="relative h-80 overflow-hidden">
                    <img
                      src={item.thumbnail || item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute inset-0 p-5 flex flex-col justify-end">
                      <span className="inline-flex items-center px-2.5 py-1 bg-white/15 backdrop-blur-sm rounded-lg text-white text-xs font-medium w-fit mb-3">
                        {item.interviewee.firstname}
                      </span>
                      <h3 className="text-lg font-bold text-white mb-2 line-clamp-2">
                        {item.title}
                      </h3>
                      <p className="text-white/80 text-xs line-clamp-2 mb-3">
                        {item.shortDescription}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-white/60 text-xs">
                          <Clock className="w-3 h-3" />
                          <span>
                            {formatDistanceToNow(new Date(item.created), {
                              addSuffix: true,
                              locale: fr
                            })}
                          </span>
                        </div>
                        <span className="text-white text-xs font-medium flex items-center gap-1">
                          Lire
                          <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>

        {interviews.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-5">
            {interviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  currentIndex === index
                    ? 'w-8 bg-gray-900 dark:bg-white'
                    : 'w-2 bg-gray-300 dark:bg-gray-600'
                }`}
                aria-label={`Recit ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="text-center mt-8">
        <Link
          to="/interviews"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white font-medium rounded-xl transition-colors text-sm"
        >
          <span>Tous les recits</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
